import { useEffect, useRef, type RefObject } from 'react';

interface Options {
  /**
   * If provided, called on every Space keydown that isn't from a typing
   * target. Useful for callers that need to suppress browser defaults
   * (e.g. pan gates Space → page scroll only when zoomed in).
   */
  onPress?: (e: KeyboardEvent) => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.matches('input, textarea, [contenteditable], [contenteditable="true"]');
}

/**
 * Tracks whether Space is currently held, regardless of focus target. The
 * returned ref reflects physical key state (so handlers can branch on it
 * inside pointer-down) — typing in an editor still produces space chars.
 *
 * The window `blur` listener resets the ref when the tab loses focus mid-press
 * so the user doesn't return to a latched-on state.
 */
export function useSpaceKeyState({ onPress }: Options = {}): RefObject<boolean> {
  const isSpacePressedRef = useRef(false);
  const onPressRef = useRef(onPress);
  onPressRef.current = onPress;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      isSpacePressedRef.current = true;
      if (isTypingTarget(e.target)) return;
      onPressRef.current?.(e);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      isSpacePressedRef.current = false;
    };
    const onBlur = () => {
      isSpacePressedRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return isSpacePressedRef;
}
